import { useState } from 'react';

import { useAuth } from '../../providers/auth-provider';
import { useTenants } from '../../hooks/use-tenants';
import { useProxyVaultConnections, useProxyVaultOverview } from '../../hooks/use-proxy-vault';

import { AddConnectionModal } from './AddConnectionModal';
import { OrgTreePicker } from './OrgTreePicker';
import { TrafficPane } from './TrafficPane';
import {
  Card,
  Empty,
  Metric,
  Pill,
  StatusDot,
  Tabs,
  Td,
  Th,
  inputStyle,
  mono,
  primaryButton,
  selectStyle,
  tableStyle,
  theadRowStyle,
  bindingRollUp,
  connectionLabel,
} from './ui';

import type { ProxyVaultConnection } from '../../lib/api-client';
import type { OpenTabFn } from '../../shell/types';

/**
 * The Proxy Vault section: three pages over one organization subtree.
 *
 * Overview answers "what is this vault doing"; Connections is the list, and a
 * row opens that connection as its OWN workspace tab rather than a drawer,
 * because a connection has four pages of its own and a popover cannot hold
 * them; Traffic is everything through the proxy, matched or not.
 */
export function ProxyVaultView({ openTab }: { openTab: OpenTabFn }) {
  const [page, setPage] = useState<'overview' | 'connections' | 'traffic'>('overview');

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <div
        style={{
          padding: '14px 20px 10px',
          display: 'flex',
          alignItems: 'baseline',
          gap: 10,
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Proxy Vault</div>
        <div style={{ fontSize: 11, color: 'var(--text-soft)' }}>
          Your credentials are in custody. Your skills use them; the model never sees them.
        </div>
      </div>

      <Tabs
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'connections', label: 'Connections' },
          { id: 'traffic', label: 'Traffic' },
        ]}
        active={page}
        onChange={(id) => setPage(id as typeof page)}
      />

      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {page === 'overview' && <OverviewPage />}
        {page === 'connections' && <ConnectionsPage openTab={openTab} />}
        {page === 'traffic' && <TrafficPane />}
      </div>
    </div>
  );
}

function OverviewPage() {
  const { overview, status } = useProxyVaultOverview();

  if (status === 'loading' && !overview) return <Empty>Loading…</Empty>;
  if (!overview) return <Empty>The overview could not be loaded.</Empty>;

  const { connections, last_24h: last, custody, top_destinations: top } = overview;
  const maxExchanges = Math.max(1, ...top.map((d) => d.exchanges));

  return (
    <div style={{ padding: 20, overflowY: 'auto', height: '100%' }} data-testid="pv-overview">
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <Card title="Connections">
          <Metric value={connections.active} label="active" />
          <Metric value={connections.incomplete} label="incomplete" />
          <Metric value={connections.organizations} label="organizations (subtree)" />
        </Card>
        <Card title="Last 24 hours">
          <Metric value={last.exchanges.toLocaleString()} label="exchanges" />
          <Metric
            value={`${last.exchanges ? Math.round((last.passthrough / last.exchanges) * 100) : 0}%`}
            label="passthrough"
          />
          <Metric value={`${last.reveals} · ${last.redactions}`} label="reveals · redactions" />
          <Metric value={`${last.refused} · ${last.tcp_lanes}`} label="refused · TCP lanes" />
        </Card>
        <Card title="Custody">
          <Metric value={custody.secrets_held} label="secrets held" />
          <div style={{ fontSize: 10.5, color: 'var(--text-mute)', marginTop: 8, lineHeight: 1.4 }}>
            Real values exist only in the vault and on the wire to a matched destination.
          </div>
        </Card>
      </div>

      <div style={{ fontSize: 11, color: 'var(--text-soft)', marginBottom: 8 }}>
        Top destinations (24h)
      </div>
      {top.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-mute)' }}>No traffic yet.</div>
      )}
      {top.map((d) => (
        <div key={d.host} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <div style={{ width: 240, fontFamily: mono, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {d.host}
          </div>
          <div
            style={{
              height: 8,
              borderRadius: 4,
              background: 'var(--accent)',
              width: `${Math.max(2, (d.exchanges / maxExchanges) * 100)}%`,
              maxWidth: 420,
            }}
          />
          <div style={{ fontSize: 11, color: 'var(--text-soft)' }}>{d.exchanges.toLocaleString()}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * Status from the viewer's seat, with the subtree roll-up beside it.
 *
 * "Incomplete" alone was the wrong word for a parent that publishes a connection
 * for its children to fill in: the children are bound and working, only the
 * parent holds nothing. So on a children-visible connection the label is the
 * roll-up — `0 main · 4 children` — and the missing count says whose it is.
 */
function StatusCell({ connection: c }: { connection: ProxyVaultConnection }) {
  const rollUp = bindingRollUp(c);
  return (
    <>
      <StatusDot status={c.status} {...(rollUp ? { label: rollUp.text } : {})} />
      {c.status === 'incomplete' && c.unbound.length > 0 && (
        <div style={{ fontSize: 10, color: 'var(--text-mute)', marginTop: 2 }} data-testid="pv-status-detail">
          {rollUp
            ? `${c.unbound.length} to bind for this org`
            : `${c.unbound.length} value${c.unbound.length === 1 ? '' : 's'} to bind`}
        </div>
      )}
    </>
  );
}

function ConnectionsPage({ openTab }: { openTab: OpenTabFn }) {
  const { user } = useAuth();
  const { connections, status, error, filters, setFilters, reload } = useProxyVaultConnections();
  const { tenants } = useTenants();
  const [addOpen, setAddOpen] = useState(false);

  // The organisation column only carries information when the page actually
  // spans more than one — otherwise it repeats the same value down every row.
  const spansOrganizations = new Set(connections.map((c) => c.tenant_id)).size > 1;

  return (
    <div style={{ height: '100%', overflowY: 'auto' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 20px',
          borderBottom: '1px solid var(--border)',
          flexWrap: 'wrap',
        }}
      >
        <input
          placeholder="Filter by name, host or variable…"
          value={filters.search}
          data-testid="pv-connections-search"
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          style={{ ...inputStyle, minWidth: 260 }}
        />
        <OrgTreePicker
          value={filters.tenantId ?? ''}
          onChange={(id) => setFilters({ ...filters, tenantId: id || null })}
          tenants={tenants}
          allOption="All organizations (subtree)"
          ariaLabel="Organization filter"
          testId="pv-connections-org"
        />
        <select
          value={filters.status}
          onChange={(e) =>
            setFilters({ ...filters, status: e.target.value as typeof filters.status })
          }
          style={selectStyle}
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="incomplete">Incomplete</option>
          <option value="revoked">Revoked</option>
        </select>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          style={{ ...primaryButton, marginLeft: 'auto' }}
          data-testid="pv-add-connection"
        >
          + Add connection
        </button>
      </div>

      {status === 'error' && (
        <div style={{ padding: 20, fontSize: 12, color: 'var(--kind-failure)' }}>
          {error ?? 'Failed to load connections.'}
        </div>
      )}
      {status === 'success' && connections.length === 0 && (
        <Empty>
          No connections yet. A connection is one vendor relationship — its routes, its values and
          its traffic. Click “Add connection” and paste its spec.
        </Empty>
      )}

      {connections.length > 0 && (
        <table style={tableStyle} data-testid="pv-connections-table">
          <thead>
            <tr style={theadRowStyle}>
              <Th first>Name</Th>
              <Th>Hosts</Th>
              <Th>Visibility</Th>
              {spansOrganizations && <Th>Organization</Th>}
              <Th>Environment</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {connections.map((c) => (
              <tr
                key={c.id}
                data-testid="pv-connection-row"
                onClick={() =>
                  openTab(
                    'proxy-vault-connection',
                    { connectionId: c.id, tenantId: c.tenant_id },
                    connectionLabel(c),
                  )
                }
                style={{ borderBottom: '1px solid var(--border-soft)', cursor: 'pointer' }}
              >
                <Td first>
                  <div style={{ fontWeight: 500 }}>{connectionLabel(c)}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-mute)', marginTop: 2 }}>
                    {/* The identifier, kept visible: it is what the spec, the
                        export and every audit row agree on. */}
                    {c.display_name ? `${c.name} · ` : ''}v{c.spec_version}
                    {c.kind === 'Connector' && ' · template'}
                    {c.materialization === 'cleartext' && ' · cleartext'}
                  </div>
                </Td>
                <Td isMono>
                  {c.hosts.slice(0, 2).join(', ')}
                  {c.hosts.length > 2 && ` +${c.hosts.length - 2}`}
                </Td>
                <Td>
                  {c.visibility === 'children' ? <Pill>visible to children</Pill> : 'private'}
                </Td>
                {spansOrganizations && (
                  <Td isMono>
                    <div
                      style={{
                        color: c.tenant_id === user?.tenant_id ? 'var(--text-soft)' : 'var(--text)',
                      }}
                    >
                      {c.tenant_external_id ?? c.tenant_name ?? c.tenant_id}
                    </div>
                    {c.tenant_id !== user?.tenant_id && (
                      <div style={{ fontSize: 10, color: 'var(--text-mute)', marginTop: 2 }}>
                        child organization
                      </div>
                    )}
                  </Td>
                )}
                <Td isMono>{c.env_names.join(', ') || '—'}</Td>
                <Td>
                  <StatusCell connection={c} />
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {addOpen && (
        <AddConnectionModal
          onCancel={() => setAddOpen(false)}
          onCreated={(created) => {
            setAddOpen(false);
            reload();
            openTab(
              'proxy-vault-connection',
              { connectionId: created.id, tenantId: created.tenant_id },
              created.name,
            );
          }}
        />
      )}
    </div>
  );
}
